import type { InfiniteData, UseSuspenseInfiniteQueryOptions } from '@tanstack/react-query';
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { GoToTest } from '../types';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import type { ListResponse, SortingParams } from 'types/shared';
import { apiClient } from 'lib/api';
import queryString from 'query-string';

interface GetParams extends Partial<SortingParams> {
  apps?: string;
  limit?: number;
  offset?: number;
}

export const getGoToTest = async (
  params?: GetParams,
  signal?: AbortSignal,
): Promise<ListResponse<GoToTest>> => {
  const res = await apiClient.get<ListResponse<GoToTest>>(queryKeys.goToTest, {
    signal,
    params,
    paramsSerializer: (prms) =>
      queryString.stringify(prms, { arrayFormat: 'comma' }),
  });
  return res.data;
};

interface UseOptions<TData> {
  config?: QueryConfig<typeof getGoToTest, TData>;
  params?: GetParams;
}

export const useGoToTest = <TData = ListResponse<GoToTest>>
({
   config,
   params,
 }: UseOptions<TData> = {}) => {
  return useSuspenseQuery({
    staleTime: 0,
    refetchOnMount: true,
    ...config,
    queryFn: ({ signal }) => getGoToTest(params, signal),
    queryKey: [queryKeys.flows, params],
  });
};

interface UseInfiniteFlowsOptions {
  config?: Omit<
    UseSuspenseInfiniteQueryOptions<
      ListResponse<GoToTest>,
      unknown,
      InfiniteData<ListResponse<GoToTest>>,
      readonly unknown[],
      number
    >,
    'queryFn' | 'queryKey' | 'initialPageParam' | 'getNextPageParam'
  >;
  params?: GetParams;
}

export const useInfiniteGoToTest =
  ({
     config,
     params,
   }: UseInfiniteFlowsOptions = {}) => {
    return useSuspenseInfiniteQuery({
      ...config,
      queryFn: ({ pageParam = 1, signal }) =>
        getGoToTest(
          {
            ...params,
            offset: pageParam,
            limit: params?.limit ?? 100,
          },
          signal,
        ),
      queryKey: [queryKeys.goToTest, 'infinite', params],
      getNextPageParam: (lastPage, allPages) => {
        const totalItems = lastPage.count;
        const loadedItems = allPages.reduce(
          (acc, page) => acc + page.data.length,
          0,
        );

        if (loadedItems >= totalItems) {
          return undefined;
        }

        return allPages.length + 1;
      },
      initialPageParam: 1,
    });
  };
